<template>
  <div class="col s12 m6 request-col">
    <div class="card core-card">
      <div class="card-content">
        <div class="core-card-top">
          <div>
            <span class="card-title core-card-title">
              {{ request.title }}
            </span>

            <div class="grey-text text-darken-1 request-meta">
              Requested by
              {{ request.requestedByEmail || "Unknown" }}
            </div>

            <div v-if="request.dueDate" class="grey-text text-darken-1 request-meta">
              Required by
              {{ formatDate(request.dueDate) }}
            </div>
          </div>

          <span class="core-badge" :class="progressBadgeClass">
            {{ workflowPercent }}%
          </span>
        </div>

        <p v-if="request.notes" class="core-note request-notes">
          {{ request.notes }}
        </p>

        <!-- ===============================================
             PROGRESS
             =============================================== -->
        <div class="core-progress workflow-progress">
          <div
            class="core-progress-fill"
            :style="{
              width: `${workflowPercent}%`,
            }"
          ></div>
        </div>

        <!-- ===============================================
             WORKFLOW
             =============================================== -->
        <div class="core-checkbox-list">
          <label
            v-for="stage in workflowStages"
            :key="stage.key"
            class="workflow-checkbox"
          >
            <input
              type="checkbox"
              :checked="Boolean(request.workflow && request.workflow[stage.key])"
              :disabled="updating || stageDisabled(stage.key)"
              @change="emitStageChange(stage.key, $event.target.checked)"
            />

            <span>
              {{ stage.label }}
            </span>
          </label>
        </div>

        <!-- ===============================================
             ARTWORK UPLOAD
             SOCIAL MEDIA ONLY
             =============================================== -->
        <div v-if="canUploadArtwork" class="core-upload-block">
          <div class="file-field input-field">
            <div class="btn blue-grey darken-2">
              <span> Upload Artwork </span>

              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                :disabled="uploading"
                @change="$emit('upload-artwork', request, $event)"
              />
            </div>

            <div class="file-path-wrapper">
              <input
                class="file-path validate"
                type="text"
                placeholder="Select one or more artwork files"
                :disabled="uploading"
              />
            </div>
          </div>

          <p v-if="uploading" class="grey-text">Uploading artwork...</p>
        </div>

        <!-- ===============================================
             UPLOADED ARTWORK
             =============================================== -->
        <div v-if="hasArtwork" class="core-upload-block">
          <div class="core-inline core-small-title artwork-preview-header">
            Uploaded artwork

            <span class="core-count-badge">
              {{ artworks.length }}
            </span>
          </div>

          <div class="core-media-grid core-media-grid--compact">
            <div
              v-for="(artwork, index) in artworks"
              :key="artwork.storagePath || artwork.downloadUrl || index"
              class="core-media-item"
            >
              <button
                v-if="isImage(artwork)"
                class="core-media-thumb-button"
                type="button"
                :aria-label="`View ${artwork.fileName || 'uploaded artwork'}`"
                @click="$emit('preview', request, artwork)"
              >
                <img
                  :src="artwork.downloadUrl"
                  :alt="artwork.fileName || 'Artwork preview'"
                  class="core-media-thumb"
                />

                <span class="core-media-overlay"> View </span>
              </button>

              <a
                v-else
                :href="artwork.downloadUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="core-media-file-link"
              >
                <span class="core-strong"> PDF </span>

                <span> Open file </span>
              </a>

              <div class="core-media-file-name">
                {{ artwork.fileName || `Artwork ${index + 1}` }}
              </div>
            </div>
          </div>
        </div>

        <!-- ===============================================
             PROOF DETAILS
             =============================================== -->
        <div
          v-if="request.workflow?.artworkSentToProof && request.proofSentTo"
          class="core-helper proof-meta"
        >
          Proof sent to:
          {{ request.proofSentTo }}
        </div>
      </div>

      <!-- ===============================================
           ACTIONS
           =============================================== -->
      <div class="card-action core-actions core-actions--spread">
        <div class="core-actions">
          <button
            v-if="canSendProof"
            class="btn-small orange darken-2"
            type="button"
            :disabled="sendingProof || uploading || !hasArtwork"
            @click="$emit('send-proof', request)"
          >
            {{ sendingProof ? "Sending..." : proofButtonLabel }}
          </button>

          <button
            v-if="canCloseRequest"
            class="btn-small green darken-2"
            type="button"
            :disabled="updating || uploading || sendingProof"
            @click="$emit('close-request', request)"
          >
            Close Request
          </button>
        </div>

        <button
          class="btn-small red darken-2"
          type="button"
          :disabled="updating"
          @click="$emit('delete-request', request)"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const LOCKED_WORKFLOW_STAGES = ["venueRequest", "artworkCreated", "artworkSentToProof"];

export default {
  name: "ArtworkRequestCard",

  props: {
    request: {
      type: Object,
      required: true,
    },

    workflowStages: {
      type: Array,
      required: true,
    },

    updating: {
      type: Boolean,
      default: false,
    },

    uploading: {
      type: Boolean,
      default: false,
    },

    sendingProof: {
      type: Boolean,
      default: false,
    },

    canUploadArtwork: {
      type: Boolean,
      default: false,
    },

    canSendProof: {
      type: Boolean,
      default: false,
    },

    canApprove: {
      type: Boolean,
      default: false,
    },

    canMarkPosted: {
      type: Boolean,
      default: false,
    },

    canCloseRequest: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "toggle-stage",
    "upload-artwork",
    "send-proof",
    "close-request",
    "delete-request",
    "preview",
  ],

  computed: {
    artworks() {
      const saved = Array.isArray(this.request.artworks)
        ? this.request.artworks.filter((item) => item && item.downloadUrl)
        : [];

      if (saved.length) {
        return saved;
      }

      if (this.request.artwork && this.request.artwork.downloadUrl) {
        return [this.request.artwork];
      }

      return [];
    },

    workflowPercent() {
      if (!this.workflowStages.length) {
        return 0;
      }

      const completed = this.workflowStages.filter(
        (stage) => this.request.workflow?.[stage.key] === true
      ).length;

      return Math.round((completed / this.workflowStages.length) * 100);
    },

    progressBadgeClass() {
      if (this.workflowPercent >= 100) {
        return "green white-text";
      }

      if (this.workflowPercent >= 50) {
        return "orange white-text";
      }

      return "blue-grey white-text";
    },

    hasArtwork() {
      return this.artworks.length > 0;
    },

    proofButtonLabel() {
      return this.request.workflow?.artworkSentToProof
        ? "Resend Proof to Venue"
        : "Send Proof to Venue";
    },
  },

  methods: {
    isImage(artwork) {
      if (artwork?.contentType?.startsWith("image/")) {
        return true;
      }

      const fileName = String(artwork?.fileName || artwork?.file_name || "");

      return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
    },

    stageDisabled(stageKey) {
      if (LOCKED_WORKFLOW_STAGES.includes(stageKey)) {
        return true;
      }

      if (stageKey === "artworkApproved") {
        return !this.canApprove;
      }

      if (stageKey === "socialContentPosted") {
        return !this.canMarkPosted;
      }

      return true;
    },

    emitStageChange(stageKey, checked) {
      if (this.stageDisabled(stageKey)) {
        return;
      }

      this.$emit("toggle-stage", this.request, stageKey, checked);
    },

    formatDate(value) {
      if (!value) {
        return "";
      }

      let date = null;

      if (typeof value === "string") {
        date = new Date(`${value}T00:00:00`);
      } else if (value && typeof value.toDate === "function") {
        date = value.toDate();
      } else {
        date = new Date(value);
      }

      if (!date || Number.isNaN(date.getTime())) {
        return String(value || "");
      }

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.request-col {
  display: flex;
}

.request-meta {
  font-size: 0.9rem;
}

.request-notes {
  margin-top: 14px;
}

.workflow-progress {
  height: 12px;
  margin: 18px 0;
}

.workflow-checkbox {
  display: block;
}

.artwork-preview-header {
  margin-bottom: 10px;
}

.proof-meta {
  margin-top: 14px;
}
</style>
